/**
 * The proof page — the second address in ONDO that does not ask who is reading
 * it, and the only screen whose whole point is that it can be ignored.
 *
 * A proof bundle exists so that somebody with no ONDO account and no reason to
 * trust ONDO can confirm a line of a record existed in this form before a
 * Bitcoin block. This page offers that check in a browser, as a convenience;
 * but it is ONDO's own code running on ONDO's own address, so the way to do
 * the check without it is always on the page — folded away, because most
 * readers want the answer and not the method, and never removed, because the
 * independent method is what makes the answer worth anything.
 *
 * EVERY SENTENCE HERE IS SCANNED BY `claims.test.ts`. A witness proves a line
 * existed in this form at a time and has not changed since. It does not prove
 * the line is true, and nothing below may say otherwise.
 */
export const PROOF_COPY = {
  title: "Check a proof",
  lead:
    "A proof bundle is a small file downloaded from a work’s record on ONDO. Give it to this " +
    "page and it will tell you whether every line in it is exactly as it was when it was " +
    "written, and which Bitcoin block each one is anchored to.",

  // ── taking the bundle ─────────────────────────────────────────────────
  dropLabel: "Choose the proof bundle",
  dropHint: "A .json file named ondo-witness-…",
  pasteToggle: "Or paste its contents instead",
  pasteLabel: "Bundle contents",
  pasteAction: "Check",
  privacy: "The file is read on your own device. Nothing is sent to ONDO or anywhere else.",
  reading: "Checking…",
  clear: "Check another bundle",

  invalidTitle: "This is not a proof bundle this page can read",
  notJson: "The text is not JSON.",
  notBundle: "The file does not say it is an ONDO proof bundle.",
  unknownFormat: "The bundle is a format this page does not know.",
  noLeaves: "The bundle carries no entries.",
  badLeaf: "An entry is not shaped like a witnessed state",

  // ── the verdict ───────────────────────────────────────────────────────
  verdict: {
    cleanTitle: "Everything in this bundle checks out",
    cleanAllAnchored: "Every entry is exactly as written, and every one is anchored to Bitcoin.",
    cleanSomeWaiting:
      "Every entry is exactly as written. {waiting} not yet anchored: when this bundle was " +
      "exported, the hour had not closed or Bitcoin had not confirmed it yet. This file will " +
      "never learn more; download a fresh bundle later and check that one.",
    problemsTitle: "{failed} in this bundle {does} not check out",
    problemsDetail:
      "Something in {those} is not what was fingerprinted. It may have been altered after it " +
      "was downloaded, or the file may be damaged. The entries that failed are marked below.",
    shreddedNote:
      "{shredded} erased at the subject’s request. The fingerprint stays, so the hour still " +
      "checks, but nothing about what it recorded can be shown.",
    entriesOne: "entry",
    entriesMany: "entries",
    isOne: "is",
    isMany: "are",
    doesOne: "does",
    doesMany: "do",
    thatOne: "that entry",
    thatMany: "those entries",
  },

  forWork: "Work",
  exported: "Bundle downloaded",

  // ── the entries ───────────────────────────────────────────────────────
  entriesTitle: "What is in the bundle",
  entriesLead: "One line per witnessed state of the record, newest first.",

  // ── the hours ─────────────────────────────────────────────────────────
  hoursTitle: "The hours and their receipts",
  hoursLead:
    "Each hour’s fingerprints are folded into one root, and each root carries the root of the " +
    "hour before it. Where this bundle holds two neighbouring hours, that link is checked too.",
  hourEntries: "entries",
  chain: {
    linked: "Carries the previous hour’s root",
    broken: "Does not carry the previous hour’s root",
    "no-neighbour": "Previous hour not in this bundle",
    open: "Hour not yet closed",
  },
  anchor: {
    open: "No root yet",
    pending: "Awaiting a Bitcoin block",
    anchored: "Bitcoin block",
  },
  root: "Root",
  downloadReceipt: "Download .ots receipt",
  verifyHint: "To confirm the block yourself, with the receipt in the same folder:",

  // ── how it works ──────────────────────────────────────────────────────
  howTitle: "How this works",
  how: [
    "Every time a line of a work’s record is written on ONDO, a fingerprint of it is taken at " +
      "that moment. Once an hour, all of that hour’s fingerprints are folded into a single " +
      "one, and that single fingerprint is placed in the Bitcoin blockchain through a free, " +
      "open service called OpenTimestamps.",
    "Nothing readable ever leaves ONDO. What reaches Bitcoin is thirty-two bytes an hour, " +
      "with no name, no price, no work and no count in it.",
    "A proof bundle holds, for one work, everything needed to redo that arithmetic: each " +
      "line as it was fingerprinted, the fingerprints themselves, and the receipts that place " +
      "each hour in a block. This page redoes it. If any line had been altered since, the " +
      "fingerprints would no longer agree.",
  ],
  howMoreTitle: "What a bundle contains, exactly",
  howMore: [
    "One entry per witnessed state of the record: the registration, each history line " +
      "written or ended, each confirmation or dispute, each document, each status of a " +
      "transfer.",
    "Each entry carries the fingerprinted text (the preimage), the random salt it was " +
      "fingerprinted under, the resulting leaf hash, the path from that leaf to its hour’s " +
      "root, the root, and the OpenTimestamps receipt that places the root in a Bitcoin block " +
      "— once there is one.",
    "Under “revealed” it carries the text and salt of exactly the fields the record’s own " +
      "rules showed the person who downloaded it. A field they could not see is absent, and " +
      "its hash in the preimage still lets the entry be checked without it.",
  ],

  // ── without ONDO ──────────────────────────────────────────────────────
  withoutTitle: "Check it without ONDO",
  withoutLead:
    "This page is ONDO’s code on ONDO’s address. The check that matters is the one you can do " +
    "somewhere ONDO cannot reach, and nothing here is needed for it: every step is a hash " +
    "anybody can recompute with ordinary tools, and at no point do you have to take ONDO’s " +
    "word, or a calendar’s, for anything.",
  steps: [
    {
      title: "Check every field you were shown.",
      body:
        "For each entry under “revealed”, hex-decode its salt, append the UTF-8 bytes of its " +
        "text, and SHA-256 the lot. The result must equal the hash under the same field name " +
        "inside the preimage’s “fields”.",
      command:
        "printf '%s' \"$SALT_HEX\" | xxd -r -p > /tmp/f\n" +
        "printf '%s' \"$TEXT\" >> /tmp/f\n" +
        "shasum -a 256 /tmp/f            # must equal preimage.fields.<name>",
    },
    {
      title: "Check the leaf.",
      body:
        "Hex-decode the entry’s salt, append the UTF-8 bytes of the preimage exactly as given " +
        "(do not re-format the JSON), and SHA-256 it. The result must equal leafHash.",
      command:
        "printf '%s' \"$LEAF_SALT_HEX\" | xxd -r -p > /tmp/l\n" +
        "printf '%s' \"$PREIMAGE\" >> /tmp/l\n" +
        "shasum -a 256 /tmp/l            # must equal leafHash",
    },
    {
      title: "Climb the path to the root.",
      body:
        "Start with the leaf hash. For each step in “path”, in order: when side is “right”, " +
        "hash current followed by the step’s hash; when “left”, hash the step’s hash followed " +
        "by current. After the last step you must be holding the root. A path is never " +
        "empty; a null path means the hour has not closed yet and there is nothing further " +
        "to check today.",
      command:
        "current=$LEAF_HASH_HEX\n" +
        "# for each step, in order:\n" +
        "#   right: current=$(printf \"$current$SIBLING\" | xxd -r -p | shasum -a 256 | cut -c1-64)\n" +
        "#   left:  current=$(printf \"$SIBLING$current\" | xxd -r -p | shasum -a 256 | cut -c1-64)\n" +
        "# current must equal root",
    },
    {
      title: "Check the receipt against Bitcoin.",
      body:
        "Base64-decode the entry’s anchor.proof to an .ots file and hand it to the open-source " +
        "OpenTimestamps client, naming the root as the digest. ONDO stamped the root itself " +
        "rather than a file, so the tool needs the digest given to it, and the drag-and-drop " +
        "verifier on opentimestamps.org, which hashes a file you give it, does not apply. By " +
        "default the tool asks a Bitcoin node on your machine; “--no-bitcoin” stops at the " +
        "calendar’s word instead, which is weaker, and the tool says so. The block it names " +
        "must be at or below anchor.blockHeight.",
      command:
        "pip install opentimestamps-client\n" +
        "echo \"$ANCHOR_PROOF_BASE64\" | base64 -d > hour.ots\n" +
        "ots info hour.ots                # the digest it names must be your root\n" +
        "ots verify -d \"$ROOT_HEX\" hour.ots   # “Success! Bitcoin block N attests existence as of …”",
    },
    {
      title: "Read the block.",
      body:
        "Look up that block by height, on your node or on any block explorer. Its timestamp " +
        "is the moment the root provably existed before, and that — nothing earlier — is the " +
        "date the witness lets you claim for every entry in that hour. The entry’s own " +
        "witnessedAt is ONDO’s clock, and is inside the fingerprint; the block’s time is the " +
        "independent bound on it.",
      command: null,
    },
  ],
  otsLink: { label: "OpenTimestamps", href: "https://opentimestamps.org/" },

  limit:
    "A witness proves that a line existed in this form at the time shown and has not changed " +
    "since. It does not prove the line is true, and ONDO concludes nothing from it.",
  backToOndo: "ONDO",
} as const;

/** The one format this page reads. The bundle names it in its first field. */
export const BUNDLE_FORMAT = "ondo-witness-bundle/1";

/** Lower-case hex SHA-256, as every hash in a bundle is spelled. */
export const HEX_64 = /^[0-9a-f]{64}$/;
